import { useForm, FieldData, TextField, Icon, Box, withStyles, PartnerRequestPasswordResetRequest } from 'vopay';
import { useMemo, useRef, useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import { Button, UtilButton, Title } from 'UI';
import { useDataFetchIndicator } from '@/hooks';
import { useAlertDialog } from '@/providers/AlertDialog.provider';
import useRequestPartnerPasswordReset from '@/hooks/useApi/useRequestPartnerPasswordReset';
import useVerifyRecaptcha from '@/hooks/useApi/useVerifyRecaptcha';
import styles from '../Login.module.scss';

const cn = withStyles(styles);

// Public site key per environment (VITE_RECAPTCHA_SITE_KEY); the backend verifies with the
// matching secret. Manage keys/allowed domains at https://www.google.com/recaptcha/admin
const RECAPTCHA_SITE_KEY: string = import.meta.env.VITE_RECAPTCHA_SITE_KEY || '';

type ResetPasswordRequestProps = {
  onBackToLoginClick: () => void;
};

type FormValues = PartnerRequestPasswordResetRequest;

// Partner Portal "forgot password" — same UX as the client portal, including the reCAPTCHA gate.
export default function ResetPasswordRequest({ onBackToLoginClick }: ResetPasswordRequestProps) {
  const { t } = useTranslation('translations', { keyPrefix: 'Login.ResetPassword' });
  const { t: tLogin } = useTranslation('translations', { keyPrefix: 'Login.Form' });
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global' });
  const alertDialog = useAlertDialog();
  // Fullscreen, blocking: the user must not resubmit or navigate while a request is in flight.
  const [, setBlocking] = useDataFetchIndicator();
  const { submitting, requestPasswordReset } = useRequestPartnerPasswordReset();
  const { verifying, verifyRecaptcha } = useVerifyRecaptcha();
  const captchaRef = useRef<ReCAPTCHA>(null);
  const [isRecaptchaVerified, setIsRecaptchaVerified] = useState(false);
  const [hasRecaptchaError, setHasRecaptchaError] = useState(false);
  const [isEmailSent, setIsEmailSent] = useState(false);

  const usernameLabel = tLogin('usernameLabel', { defaultValue: 'Username' });

  const config = useMemo<FieldData<FormValues>[]>(
    () => [
      {
        fieldName: 'Username',
        getInputState: () => 'required',
        showField: () => true,
        validate: () => Yup.string().label(usernameLabel).required(),
        // Pasted/autofilled usernames often carry whitespace; the endpoint answers Success either
        // way (no enumeration), so a stray space would otherwise be a silent dead end.
        serializeField: (values) => values.Username.trim(),
        label: () => usernameLabel,
        renderField: ({ getFormikData }) => (
          <TextField
            {...getFormikData<FormValues['Username']>({})}
            variant="outlineShadow"
            placeholder={tLogin('usernamePlaceholder', { defaultValue: 'Username' })}
            elementLeft={<Icon icon="user" size="md" color="medium-dark" />}
          />
        ),
      },
    ],
    [usernameLabel, tLogin],
  );

  const { ctx, renderFormElements } = useForm<FormValues>({
    initialValues: { Username: '' },
    config,
    onSubmit: sendResetEmail,
  });

  async function sendResetEmail(values: FormValues) {
    if (!isRecaptchaVerified) {
      setHasRecaptchaError(true);

      return;
    }

    try {
      setBlocking(true);
      await requestPasswordReset(values);
      setIsEmailSent(true);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';

      alertDialog.error(
        tGlobal('error', { defaultValue: 'Error' }),
        t('Form.requestError', { defaultValue: 'Could not send the reset email: {{errorMessage}}', errorMessage }),
      );
    } finally {
      setBlocking(false);
    }
  }

  async function handleRecaptchaChange() {
    setHasRecaptchaError(false);

    // reCAPTCHA also fires onChange with no token when one expires; there is
    // nothing to verify then, so leave the form enabled for another attempt.
    const response = captchaRef.current?.getValue();

    if (!response) {
      setIsRecaptchaVerified(false);

      return;
    }

    try {
      setBlocking(true);
      await verifyRecaptcha({ response });
      setIsRecaptchaVerified(true);
    } catch (error) {
      setIsRecaptchaVerified(false);
      captchaRef.current?.reset();
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';

      alertDialog.error(
        tGlobal('error', { defaultValue: 'Error' }),
        t('Form.recaptchaError', { defaultValue: 'Could not verify the reCAPTCHA: {{errorMessage}}', errorMessage }),
      );
    } finally {
      setBlocking(false);
    }
  }

  if (isEmailSent) {
    return (
      <Box flx="col">
        <Box mb="sm" flx="col" ai="center" height="auto">
          <Title level="4">{t('Resume.title', { defaultValue: 'Email Sent' })}</Title>
        </Box>

        <div className={cn('errorText')}>
          {t('Resume.description', {
            defaultValue: 'Instructions on how to reset your password have been sent to your email address.',
          })}
        </div>

        <Box jc="fe" flx="col" height="auto">
          <UtilButton
            id="return-to-login-page"
            data-testid="return-to-login-page"
            size="large"
            onClick={onBackToLoginClick}
          >
            {t('Resume.returnButton', { defaultValue: 'Return to Login Page' })}
          </UtilButton>
        </Box>
      </Box>
    );
  }

  return (
    <Box flx="col" width="100%">
      <Box mb="sm" flx="col" ai="center" height="auto">
        <Title level="4">{t('Form.title', { defaultValue: 'Reset your password' })}</Title>
      </Box>

      <form onSubmit={ctx.handleSubmit}>
        <Box mb="sm" flx="col" height="auto">
          {renderFormElements({
            config,
            // Render label + input ourselves so the field matches the login form's markup and
            // styling (no ElementContainer chrome or required asterisk) in the shared card.
            override: ({ label, template }) => (
              <>
                <div className={cn('fieldLabel')}>{label}</div>
                {template}
              </>
            ),
          })}
        </Box>

        <div className={cn('reCAPTCHAContainer')}>
          <ReCAPTCHA sitekey={RECAPTCHA_SITE_KEY} onChange={handleRecaptchaChange} ref={captchaRef} />

          {hasRecaptchaError && (
            <div className={cn('errorMSG')}>
              {t('Form.notARobotValidation', { defaultValue: 'Please confirm you are not a robot.' })}
            </div>
          )}
        </div>

        <Box jc="fe" flx="col" height="auto">
          <Button
            id="reset-password-submit"
            type="submit"
            colorScheme="primary"
            size="large"
            className={cn('submitButton')}
            disabled={verifying || submitting || ctx.isSubmitting}
          >
            {t('Form.submitButton', { defaultValue: 'Send Password Reset Email' })}
          </Button>
        </Box>
      </form>

      <Box mt="sm" height="auto" jc="center" ai="center">
        <UtilButton
          id="forgot-password-cancel"
          data-testid="forgot-password-cancel"
          size="large"
          onClick={onBackToLoginClick}
        >
          {tGlobal('Buttons.goBack', { defaultValue: 'Go back' })}
        </UtilButton>
      </Box>
    </Box>
  );
}
